/* global fetchPositionsUrl deletePositionUrl showPositionUrl editPositionUrl importPositionUrl exportPositionUrl deleteAllPositionsUrl deleteManyPositionsUrl */
/* global fetchDepartmentsUrl createPositionUrl */
/* global canCreatePosition positionTypes */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import _ from 'lodash';

import { NOT_SET, RECRUITMENT_STATUSES_CHANGED_EVENT, notifyRecruitmentSummaryStale } from '../workforce/assumption/recruitmentStatusCategories';

import AddPositionMenu from './AddPositionMenu';
import PositionsBulkBar from './PositionsBulkBar';
import PositionsGroupRow from './PositionsGroupRow';
import PositionsTableRow from './PositionsTableRow';
import PositionStatusChips from './PositionStatusChips';
import PositionsToolbar from './PositionsToolbar';
import usePositionsColumns, { COLUMN_BUDGET_CODE, COLUMN_EMPLOYEE_NUMBER, COLUMN_END_DATE, COLUMN_UNIQUE_ID } from './usePositionsColumns';

import { apiClient } from 'Helpers/network';

import CheckboxInput from 'CommonComponents/CheckboxInput';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import PerPageSelect from 'CommonComponents/PerPageSelect';
import TablePagination from 'CommonComponents/TablePagination';
import TableSortHeaderCell from 'CommonComponents/TableSortHeaderCell';

import { ConfirmationModalProvider } from 'Context/ConfirmationModalContext';

import useApiRequest from 'Hooks/useApiRequest';
import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import usePagination from 'Hooks/usePagination';
import useSort from 'Hooks/useSort';
import { useToast } from 'Hooks/useToast';

import ChevronLeftIcon from 'Assets/icons/chevron-left.svg';
import ChevronRightIcon from 'Assets/icons/chevron-right.svg';
import FileExportIcon from 'Assets/icons/file-export.svg';
import FileIcon from 'Assets/icons/file.svg';

const SORT_NAME = 'name';
const SORT_TYPE = 'type';
const SORT_RECRUITMENT_STATUS = 'recruitment_status';
const SORT_START_DATE = 'start_date';
const SORT_END_DATE = 'end_date';
const SORT_TOTAL = 'total';
const SORT_PAY_RATE = 'pay_rate';
const SORT_COMMITTED = 'committed';
const SORT_UNIQUE_ID = 'unique_id';
const SORT_REFERENCE_NUMBER = 'reference_number';
const SORT_EMPLOYEE_NUMBER = 'employee_number';
const SORT_SUPERVISOR_REFERENCE = 'supervisor_reference';

// The bulk endpoint accepts at most this many positions per request.
const MAX_BULK_STATUS_POSITIONS = 500;

// The multi-delete endpoint refuses more than this many positions.
const MAX_DELETE_POSITIONS = 500;

const SORT_DEPARTMENT_COST_CENTRE = 'department_cost_centre';

const PER_PAGE_OPTIONS = [25, 50, 100];

const EMPTY_FILTERS = { department: '', type: '', recruitmentStatus: '', committed: '' };

const rowPositionIds = (row) => (row.kind === 'group' ? row.members.map((member) => member.id) : [row.position.id]);

export default function PositionsTable({
    fetchUrl,
    deleteUrl,
    deleteAllUrl,
    deleteManyUrl,
    showUrl,
    editUrl,
    importUrl,
    exportUrl,
    createUrl,
    fetchDepartmentsUrl,
    canCreatePosition,
    positionTypes,
    recruitmentStatusesUrl,
    bulkRecruitmentStatusUrl,
}) {
    const [showVenues, setShowVenues] = useState(false);
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [category, setCategory] = useState('');
    const [recruitmentStatuses, setRecruitmentStatuses] = useState([]);
    const [isApplyingStatus, setIsApplyingStatus] = useState(false);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    // Groups the user opened, and groups the current search opened; a group is open in either.
    const [manualExpanded, setManualExpanded] = useState(() => new Set());
    const [autoExpanded, setAutoExpanded] = useState(() => new Set());
    // Search-opened groups the user closed; they stay closed until the search changes.
    const autoCollapsedRef = useRef(new Set());
    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const paginationRef = useRef(null);
    const pagination = usePagination({ perPage: PER_PAGE_OPTIONS[0] });

    paginationRef.current = pagination;

    const sort = useSort();

    const toast = useToast();

    const { get: fetchPositions, data, loading } = useApiRequest({ url: fetchUrl });
    const departmentsRequest = useApiRequest({ url: fetchDepartmentsUrl });

    const { columns, toggle: toggleColumn, definitions: columnDefinitions } = usePositionsColumns(data ? (data.meta?.columns ?? null) : undefined);

    const confirmDeletePosition = useConfirmationModal({ title: $t('Delete position') });
    const confirmDeleteAllPositions = useConfirmationModal({ title: $t('Delete All Positions') });
    const confirmDeleteManyPositions = useConfirmationModal({ title: $t('Delete selected positions') });

    const readOnly = !!window.budgetReadOnly;
    const showRecruitmentStatus = !!bulkRecruitmentStatusUrl;
    const statusEditable = showRecruitmentStatus && !readOnly;

    // Every filter, the chips, the search and the page size go back to page 1 with nothing selected.
    const resetPageAndSelection = () => {
        setSelectedIds(new Set());
        paginationRef.current.setPage(1);
    };

    const debounceSearch = useRef(
        _.debounce((value) => {
            ReactDOM.unstable_batchedUpdates(() => {
                setDebouncedSearch(value);
                setSelectedIds(new Set());
                paginationRef.current.setPage(1);
            });
        }, 400),
    );

    useEffect(() => {
        return () => debounceSearch.current.cancel();
    }, []);

    useEffect(() => {
        // Cost centres too, for the Add Position picker.
        departmentsRequest.get({ loadCostcenters: 1 });
    }, []);

    // Read from a window listener, so they must always hold the current values.
    const refreshListRef = useRef(null);
    const recruitmentStatusFilterRef = useRef(filters.recruitmentStatus);
    recruitmentStatusFilterRef.current = filters.recruitmentStatus;

    useEffect(() => {
        if (!recruitmentStatusesUrl) {
            return undefined;
        }

        const fetchStatuses = () =>
            apiClient
                .get(recruitmentStatusesUrl)
                .then(({ data }) => {
                    setRecruitmentStatuses(data.data);
                    return data.data;
                })
                .catch(() => {
                    setRecruitmentStatuses([]);
                    return null;
                });

        fetchStatuses();

        // Statuses are managed on the Assumptions tab of the same page; a delete
        // there can also move rows here to another status.
        const handleStatusesChanged = () => {
            fetchStatuses().then((statuses) => {
                const filter = recruitmentStatusFilterRef.current;
                const filterWasDeleted =
                    statuses !== null && filter !== '' && filter !== NOT_SET && !statuses.some((status) => String(status.id) === String(filter));

                if (filterWasDeleted) {
                    // Changing the filter refetches the list; the deleted id would be refused.
                    // Batched so the reset triggers one fetch, not one per state update.
                    ReactDOM.unstable_batchedUpdates(() => {
                        setFilters((prev) => ({ ...prev, recruitmentStatus: '' }));
                        setSelectedIds(new Set());
                        paginationRef.current.setPage(1);
                    });

                    return;
                }

                refreshListRef.current?.();
            });
        };

        window.addEventListener(RECRUITMENT_STATUSES_CHANGED_EVENT, handleStatusesChanged);

        return () => window.removeEventListener(RECRUITMENT_STATUSES_CHANGED_EVENT, handleStatusesChanged);
    }, [recruitmentStatusesUrl]);

    useEffect(() => {
        debounceSearch.current(searchInput);
    }, [searchInput]);

    useEffect(() => {
        autoCollapsedRef.current = new Set();
    }, [debouncedSearch]);

    const rows = data?.rows?.data ?? [];

    useEffect(() => {
        if (!data) {
            return;
        }

        const { from, to, total, last_page } = data.rows;

        pagination.setTotal(total);
        pagination.setLastPage(last_page);
        pagination.setFrom(from);
        pagination.setTo(to);

        // Replaced on every load, so clearing the search closes what it opened.
        setAutoExpanded(
            new Set(data.rows.data.filter((row) => row.kind === 'group' && row.auto_expand && !autoCollapsedRef.current.has(row.id)).map((row) => row.id)),
        );

        // A refetch that drops rows (another page, or a status change leaving the filter) drops their selection.
        const loadedIds = new Set(data.rows.data.flatMap(rowPositionIds));
        setSelectedIds((prev) => ([...prev].every((id) => loadedIds.has(id)) ? prev : new Set([...prev].filter((id) => loadedIds.has(id)))));
    }, [data]);

    const buildListParams = (pageOverride) => ({
        page: pageOverride ?? pagination.page,
        per_page: pagination.perPage,
        ...(filters.department ? { department: filters.department } : {}),
        ...(filters.type ? { type: filters.type } : {}),
        ...(filters.committed ? { committed: filters.committed } : {}),
        ...(showRecruitmentStatus && filters.recruitmentStatus ? { recruitment_status: filters.recruitmentStatus } : {}),
        ...(category ? { recruitment_category: category } : {}),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(sort.type && sort.dir ? { sort_by: sort.type, sort_dir: sort.dir } : {}),
    });

    refreshListRef.current = () => fetchPositions(buildListParams());

    useEffect(() => {
        fetchPositions(buildListParams());
    }, [pagination.page, pagination.perPage, filters, category, debouncedSearch, sort.type, sort.dir]);

    const pageIds = useMemo(() => rows.flatMap(rowPositionIds), [rows]);
    const pageGroupIds = useMemo(() => rows.filter((row) => row.kind === 'group').map((row) => row.id), [rows]);
    const selectedOnPage = pageIds.filter((id) => selectedIds.has(id)).length;
    const isExpanded = (groupId) => manualExpanded.has(groupId) || autoExpanded.has(groupId);
    const allGroupsExpanded = pageGroupIds.length > 0 && pageGroupIds.every(isExpanded);

    const positionsById = useMemo(
        () => new Map(rows.flatMap((row) => (row.kind === 'group' ? row.members : [row.position])).map((position) => [position.id, position])),
        [rows],
    );
    const selectionDeletable = [...selectedIds].every((id) => positionsById.get(id)?.can?.delete);

    // Without status features every position reads as Not set, so the chips can only count.
    const categoryCounts = showRecruitmentStatus ? data?.category_counts : { all: data?.category_counts?.all ?? 0, [NOT_SET]: data?.category_counts?.all ?? 0 };

    const changeFilter = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        resetPageAndSelection();
    };

    const changeCategory = (value) => {
        setCategory(value);
        resetPageAndSelection();
    };

    const toggleSort = (type) => {
        sort.toggleSort(type);
        setSelectedIds(new Set());
    };

    const selectIds = (ids, checked) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            ids.forEach((id) => (checked ? next.add(id) : next.delete(id)));

            return next;
        });
    };

    const setGroupsExpanded = (groupIds, expanded) => {
        const update = (prev) => {
            const next = new Set(prev);
            groupIds.forEach((id) => (expanded ? next.add(id) : next.delete(id)));

            return next;
        };

        setManualExpanded(update);
        if (!expanded) {
            groupIds.forEach((id) => autoCollapsedRef.current.add(id));
            setAutoExpanded(update);
        }
    };

    const toggleGroup = (groupId) => setGroupsExpanded([groupId], !isExpanded(groupId));

    const toggleAllGroups = () => setGroupsExpanded(pageGroupIds, !allGroupsExpanded);

    // Back to page 1 with nothing selected; one fetch whether or not the page changes.
    const reloadFromFirstPage = () => {
        setSelectedIds(new Set());

        if (paginationRef.current.page === 1) {
            refreshListRef.current();
        } else {
            paginationRef.current.setPage(1);
        }
    };

    const reportFailure = (error, fallback) => {
        if (error?.response?.status === 403 && error.response.data?.message) {
            return toast.error(error.response.data.message);
        }

        toast.error(fallback);
    };

    const handleDelete = (position) => {
        confirmDeletePosition({
            message: $t('Are you sure you want to delete {{name}}?', { name: position.name }),
            onConfirm: () => {
                axios
                    .delete(deleteUrl.replace('POSITION_ID', position.id))
                    .then(() => {
                        toast.success($t('{{name}} deleted', { name: position.name }));

                        reloadFromFirstPage();
                    })
                    .catch((error) => reportFailure(error, $t('Failed to delete position')));
            },
        });
    };

    const handleDeleteAll = () => {
        confirmDeleteAllPositions({
            message: $t('Are you sure you want to delete all positions?'),
            onConfirm: () => {
                axios
                    .post(deleteAllUrl)
                    .then((response) => {
                        toast.success(response?.data?.message || $t('Positions successfully deleted'));

                        reloadFromFirstPage();
                    })
                    .catch((error) => reportFailure(error, $t('Failed to delete positions')));
            },
        });
    };

    const handleDeleteSelected = () => {
        const ids = [...selectedIds];

        if (ids.length > MAX_DELETE_POSITIONS) {
            toast.warning($t('You can delete up to {{max}} positions at a time', { max: MAX_DELETE_POSITIONS }));
            return;
        }

        confirmDeleteManyPositions({
            message: $t('Delete {{count}} positions?', { count: ids.length }),
            onConfirm: () => {
                apiClient
                    .post(deleteManyUrl, { deleteIds: ids })
                    .then((response) => {
                        toast.success(response?.data?.message || $t('Positions successfully deleted'));

                        reloadFromFirstPage();
                    })
                    .catch((error) => reportFailure(error, $t('Failed to delete positions')));
            },
        });
    };

    // One request per chunk, in turn; the counts are summed into a single toast.
    const applyStatus = async (ids, statusId, { clearSelection = false } = {}) => {
        if (!ids.length) {
            return;
        }

        setIsApplyingStatus(true);

        try {
            let updated = 0;
            let skipped = 0;

            for (const chunk of _.chunk(ids, MAX_BULK_STATUS_POSITIONS)) {
                const { data: result } = await apiClient.post(bulkRecruitmentStatusUrl, { position_ids: chunk, recruitment_status_id: statusId });
                updated += result.updated;
                skipped += result.skipped;
            }

            const message = $t('{{updated}} updated, {{skipped}} skipped', { updated, skipped });
            if (skipped > 0) {
                toast.warning(message);
            } else {
                toast.success(message);
            }

            if (clearSelection) {
                setSelectedIds(new Set());
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || $t('Failed to update the recruitment status'));
        } finally {
            setIsApplyingStatus(false);
            refreshListRef.current();
            notifyRecruitmentSummaryStale();
        }
    };

    const rowProps = {
        types: positionTypes,
        columns,
        showVenues,
        venues: data?.venues,
        showUrl,
        editUrl,
        selectable: !readOnly,
        onStatusChange: (ids, statusId) => applyStatus(ids, statusId),
        onDelete: handleDelete,
    };

    const sortHeader = (type, label, className) => (
        <th className={`tw-cursor-pointer ${className ?? ''}`} onClick={() => toggleSort(type)}>
            <TableSortHeaderCell asc={sort.isAsc(type)} desc={sort.isDesc(type)}>
                {label}
            </TableSortHeaderCell>
        </th>
    );

    return (
        <div className="table__card tw-space-y-3">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                <h4 className="table__header !tw-mb-0">{$t('Positions')}</h4>
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                    {canCreatePosition && (
                        <a className="button outline" href={importUrl} data-action="navigate">
                            <FileIcon className="tw-w-3.5 tw-h-3.5" />
                            {$t('Import from file')}
                        </a>
                    )}
                    <a className="button outline" href={exportUrl} data-action="navigate">
                        <FileExportIcon className="tw-w-3.5 tw-h-3.5" />
                        {$t('Export to file')}
                    </a>
                    {canCreatePosition && !readOnly && !!createUrl && (
                        <AddPositionMenu createUrl={createUrl} departments={departmentsRequest.data?.departments ?? []} positionTypes={positionTypes} />
                    )}
                </div>
            </div>

            <PositionsToolbar
                search={searchInput}
                onSearchChange={setSearchInput}
                departments={departmentsRequest.data?.departments ?? []}
                filters={filters}
                onFilterChange={changeFilter}
                positionTypes={positionTypes}
                statuses={recruitmentStatuses}
                showStatusFilter={showRecruitmentStatus}
                columns={columns}
                columnDefinitions={columnDefinitions}
                onToggleColumn={toggleColumn}
                showDeleteAll={!readOnly && !!data?.meta?.can_delete_all && !!data?.rows?.total}
                onDeleteAll={handleDeleteAll}
            />

            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
                <PositionStatusChips counts={categoryCounts} selected={category} interactive={showRecruitmentStatus} onSelect={changeCategory} />
                <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-charcoal-75">
                    <span className="tw-inline-flex tw-items-center tw-rounded tw-bg-amber-100 tw-px-1.5 tw-py-0.5 tw-font-medium tw-text-amber-800">
                        {$t('Override')}
                    </span>
                    <span>= {$t('Manually entered salary overrides the central assumption pay rate')}</span>
                </div>
            </div>

            {!readOnly && selectedIds.size > 0 && (
                <PositionsBulkBar
                    count={selectedIds.size}
                    statuses={recruitmentStatuses}
                    showStatus={statusEditable}
                    isApplying={isApplyingStatus}
                    onApplyStatus={(statusId) => applyStatus([...selectedIds], statusId, { clearSelection: true })}
                    canDelete={selectionDeletable}
                    onDelete={handleDeleteSelected}
                    onClear={() => setSelectedIds(new Set())}
                />
            )}

            <div className="inbud-table_wrapper">
                <table className="inbud-table [&_td]:tw-whitespace-nowrap [&_th]:tw-whitespace-nowrap">
                    <thead>
                        <tr>
                            <th className="tw-w-8">
                                {!readOnly && !!pageIds.length && (
                                    <CheckboxInput
                                        aria-label={$t('Select all on this page')}
                                        checked={selectedOnPage === pageIds.length}
                                        indeterminate={selectedOnPage > 0 && selectedOnPage < pageIds.length}
                                        onChange={(e) => selectIds(pageIds, e.target.checked)}
                                    />
                                )}
                            </th>
                            <th className="tw-min-w-[14rem]">
                                <div className="tw-flex tw-items-center tw-gap-x-3">
                                    <button type="button" onClick={() => toggleSort(SORT_NAME)} aria-label={$t('Sort by position')}>
                                        <TableSortHeaderCell asc={sort.isAsc(SORT_NAME)} desc={sort.isDesc(SORT_NAME)}>
                                            {$t('Position')}
                                        </TableSortHeaderCell>
                                    </button>
                                    <button
                                        type="button"
                                        className="tw-text-2xs tw-font-medium tw-text-charcoal-50"
                                        onClick={() => toggleSort(SORT_DEPARTMENT_COST_CENTRE)}
                                        aria-label={$t('Sort by department / cost centre')}
                                        title={$t('Sort by department / cost centre')}
                                    >
                                        <TableSortHeaderCell asc={sort.isAsc(SORT_DEPARTMENT_COST_CENTRE)} desc={sort.isDesc(SORT_DEPARTMENT_COST_CENTRE)}>
                                            {$t('Dept')}
                                        </TableSortHeaderCell>
                                    </button>
                                </div>
                            </th>
                            {sortHeader(SORT_TYPE, $t('Type'))}
                            {showRecruitmentStatus ? (
                                sortHeader(SORT_RECRUITMENT_STATUS, $t('Recruitment status'), 'tw-min-w-[10rem]')
                            ) : (
                                <th className="tw-min-w-[10rem]">{$t('Recruitment status')}</th>
                            )}
                            {sortHeader(SORT_START_DATE, $t('Start'))}
                            {columns[COLUMN_END_DATE] && sortHeader(SORT_END_DATE, $t('End'))}
                            {sortHeader(SORT_PAY_RATE, $t('Pay rate'))}
                            {sortHeader(SORT_TOTAL, $t('Total cost'), 'inbud-numeric-cell')}
                            {sortHeader(SORT_COMMITTED, $t('Committed'))}
                            {sortHeader(SORT_SUPERVISOR_REFERENCE, $t('Supervisor'))}
                            {columns[COLUMN_UNIQUE_ID] && sortHeader(SORT_UNIQUE_ID, $t('Unique Id'))}
                            {columns[COLUMN_BUDGET_CODE] && sortHeader(SORT_REFERENCE_NUMBER, $t('Budget Code'))}
                            {columns[COLUMN_EMPLOYEE_NUMBER] && sortHeader(SORT_EMPLOYEE_NUMBER, $t('Employee Number'))}
                            <th className="inbud-numeric-cell">
                                <div className="tw-inline-flex tw-items-center tw-gap-x-2">
                                    {$t('Venue allocation')}
                                    {!!data?.venues?.length && (
                                        <button
                                            type="button"
                                            onClick={() => setShowVenues((prev) => !prev)}
                                            aria-expanded={showVenues}
                                            title={showVenues ? $t('Hide venues') : $t('Show venues')}
                                        >
                                            {showVenues ? <ChevronLeftIcon className="tw-h-3" /> : <ChevronRightIcon className="tw-h-3" />}
                                        </button>
                                    )}
                                </div>
                            </th>
                            {showVenues &&
                                !!data?.venues?.length &&
                                data.venues.map((venue) => (
                                    <th key={venue.id} className="inbud-numeric-cell">
                                        {venue.name}
                                    </th>
                                ))}
                            <th className="tw-w-24" aria-label={$t('Actions')} />
                        </tr>
                    </thead>

                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="100%" className="tw-p-4">
                                    <div className="tw-flex tw-justify-center tw-items-center">
                                        <LoadingSpinner />
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            rows.map((row) =>
                                row.kind === 'group' ? (
                                    <PositionsGroupRow
                                        key={row.id}
                                        group={row}
                                        expanded={isExpanded(row.id)}
                                        onToggleExpanded={() => toggleGroup(row.id)}
                                        selectedIds={selectedIds}
                                        onSelectIds={selectIds}
                                        statuses={recruitmentStatuses}
                                        statusEditable={statusEditable}
                                        statusSaving={isApplyingStatus}
                                        showRecruitmentStatus={showRecruitmentStatus}
                                        rowProps={rowProps}
                                    />
                                ) : (
                                    <PositionsTableRow
                                        key={row.id}
                                        {...rowProps}
                                        position={row.position}
                                        selected={selectedIds.has(row.position.id)}
                                        statuses={recruitmentStatuses}
                                        statusEditable={statusEditable}
                                        statusSaving={isApplyingStatus}
                                        showRecruitmentStatus={showRecruitmentStatus}
                                        onSelect={(checked) => selectIds([row.position.id], checked)}
                                    />
                                ),
                            )
                        )}
                    </tbody>
                </table>
            </div>

            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-text-xs tw-text-charcoal-75">
                <div className="tw-flex tw-items-center tw-gap-x-1.5">
                    <span>
                        {$t('Showing {{from}}–{{to}} of {{total}}', {
                            from: data?.rows?.from ?? 0,
                            to: data?.rows?.to ?? 0,
                            total: data?.rows?.total ?? 0,
                        })}
                    </span>
                    {!!pageGroupIds.length && (
                        <>
                            <span aria-hidden="true">·</span>
                            <button type="button" className="tw-text-blue tw-underline" onClick={toggleAllGroups}>
                                {allGroupsExpanded ? $t('Collapse all groups') : $t('Expand all groups')}
                            </button>
                        </>
                    )}
                </div>
                <div className="tw-flex tw-items-center tw-gap-3">
                    <PerPageSelect
                        label={$t('Rows:')}
                        value={pagination.perPage}
                        options={PER_PAGE_OPTIONS}
                        onChange={(e) => {
                            setSelectedIds(new Set());
                            pagination.setPerPage(parseInt(e.target.value, 10));
                        }}
                    />
                    <TablePagination
                        lastPage={pagination.lastPage}
                        pageChange={(page) => {
                            setSelectedIds(new Set());
                            pagination.setPage(+page.selected + 1);
                        }}
                        currentPage={pagination.page}
                    />
                </div>
            </div>
        </div>
    );
}

if (document.getElementById('PositionsTable')) {
    var root = document.getElementById('PositionsTable');
    ReactDOM.render(
        <App>
            <ConfirmationModalProvider>
                <PositionsTable
                    fetchUrl={fetchPositionsUrl}
                    deleteUrl={deletePositionUrl}
                    deleteAllUrl={deleteAllPositionsUrl}
                    deleteManyUrl={deleteManyPositionsUrl}
                    showUrl={showPositionUrl}
                    editUrl={editPositionUrl}
                    importUrl={importPositionUrl}
                    exportUrl={exportPositionUrl}
                    createUrl={createPositionUrl}
                    fetchDepartmentsUrl={fetchDepartmentsUrl}
                    canCreatePosition={canCreatePosition}
                    positionTypes={positionTypes}
                    recruitmentStatusesUrl={root.dataset.recruitmentStatusesUrl || null}
                    bulkRecruitmentStatusUrl={root.dataset.bulkRecruitmentStatusUrl || null}
                />
            </ConfirmationModalProvider>
        </App>,
        root,
    );
}
